import React, { useState } from "react";
import { Loader2, Share2, X } from "lucide-react";
import { toast } from "sonner";
import ShareActions from "components/Common/ShareActions";
import EntityAvatar from "components/Entities/EntityAvatar";
import InputField from "ui/InputField";
import { useUser } from "util/UserContext";
import type { QuizDetail } from "types/quiz";
import type { QuizUiStrings } from "i18n/quizUi";
import { getQuizButtonClassName, mapQuizUserToEntityAvatarUser } from "./quizUtils";

interface QuizLobbyProps {
  ui: QuizUiStrings;
  quiz: QuizDetail;
  qrUrl: string | null;
  shareUrl: string;
  onStart: () => void;
  onSendInvites: (emails: string[]) => Promise<{ sentCount: number; failedEmails: string[] }>;
  isStarting: boolean;
  isSendingInvites: boolean;
}

/**
 * Share-first lobby while the creator gathers players.
 */
const QuizLobby: React.FC<QuizLobbyProps> = ({
  ui,
  quiz,
  qrUrl,
  shareUrl,
  onStart,
  onSendInvites,
  isStarting,
  isSendingInvites,
}) => {
  const code = quiz.quiz.code;
  const { currentUser } = useUser();
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState<string[]>([]);

  const normalizeEmail = (value: string): string => value.trim().toLowerCase();

  const addEmail = (rawValue: string): boolean => {
    const normalized = normalizeEmail(rawValue);
    if (!normalized) {
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(normalized)) {
      toast.error(ui.inviteEmailsInvalid);
      return false;
    }
    if (emails.includes(normalized)) {
      return false;
    }
    if (emails.length >= 20) {
      toast.error(ui.inviteEmailsTooMany);
      return false;
    }
    setEmails((current) => [...current, normalized]);
    return true;
  };

  const collectInviteEmails = (): string[] | null => {
    const normalizedDraft = normalizeEmail(emailInput);
    if (!normalizedDraft) {
      return emails;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/i.test(normalizedDraft)) {
      toast.error(ui.inviteEmailsInvalid);
      return null;
    }
    const merged = Array.from(new Set([...emails, normalizedDraft]));
    if (merged.length > 20) {
      toast.error(ui.inviteEmailsTooMany);
      return null;
    }
    return merged;
  };

  const flushEmailInput = () => {
    if (!emailInput.trim()) {
      return;
    }
    if (addEmail(emailInput)) {
      setEmailInput("");
    }
  };

  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 px-4 py-8">
      <div className="space-y-2 text-center">
        <h1 className="text-3xl font-semibold text-eoe-text-heading dark:text-eoe-dark-text-heading">
          {ui.lobbyTitle}
        </h1>
        <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.lobbyDescription}</p>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.1fr_0.9fr]">
        <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 shadow-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
          <div className="flex flex-col items-center gap-4">
            <div className="w-full rounded-2xl border border-eoe-border-main bg-eoe-bg-hover p-4 dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-hover">
              <div className="space-y-3">
                <div className="space-y-1">
                  <h2 className="text-base font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.inviteEmailsTitle}</h2>
                  <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.inviteEmailsDescription}</p>
                </div>

                <InputField
                  type="email"
                  labelText={ui.inviteEmailsLabel}
                  fieldPlaceholder={ui.inviteEmailsPlaceholder}
                  value={emailInput}
                  onChange={(event) => setEmailInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === "," || event.key === "Tab") {
                      event.preventDefault();
                      flushEmailInput();
                    }
                  }}
                  onBlur={() => {
                    flushEmailInput();
                  }}
                />

                {emails.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {emails.map((email) => (
                      <span
                        key={email}
                        className="inline-flex items-center gap-2 rounded-full bg-eoe-icon px-3 py-1.5 text-sm text-eoe-text dark:bg-eoe-dark-icon dark:text-eoe-dark-text"
                      >
                        {email}
                        <button
                          type="button"
                          onClick={() => setEmails((current) => current.filter((value) => value !== email))}
                          className="rounded-full p-0.5 hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-bg-hover"
                          aria-label={`Remove ${email}`}
                        >
                          <X className="size-3" aria-hidden />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : null}

                <button
                  type="button"
                  disabled={isSendingInvites || (emails.length === 0 && emailInput.trim().length === 0)}
                  onClick={() => {
                    const currentEmails = collectInviteEmails();
                    if (!currentEmails || currentEmails.length === 0) {
                      return;
                    }
                    void onSendInvites(currentEmails)
                      .then((result) => {
                        if (result.failedEmails.length > 0) {
                          toast.warning(ui.inviteEmailsPartial);
                        } else {
                          toast.success(ui.inviteEmailsSuccess);
                        }
                        setEmails([]);
                        setEmailInput("");
                      })
                      .catch(() => {
                        toast.error(ui.genericSaveError);
                      });
                  }}
                  className={getQuizButtonClassName({
                    disabled: Boolean(isSendingInvites || (emails.length === 0 && emailInput.trim().length === 0)),
                    fullWidth: true,
                  })}
                >
                  {isSendingInvites ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                  {ui.inviteEmailsSendAction}
                </button>

                {currentUser ? (
                  <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    {`${currentUser.display_name || currentUser.username} (@${currentUser.username})`}
                  </p>
                ) : null}
              </div>
            </div>
            <div className="overflow-hidden rounded-2xl border border-eoe-border-main bg-white p-3 dark:border-eoe-dark-border-main">
              {qrUrl ? (
                <img
                  src={qrUrl}
                  alt={`${ui.codeLabel}: ${code}`}
                  className="h-64 w-64 object-contain"
                />
              ) : (
                <div className="flex h-64 w-64 items-center justify-center bg-white text-sm text-slate-500">
                  {ui.loadingQuiz}
                </div>
              )}
            </div>

            <div className="space-y-1 text-center">
              <p className="text-sm font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.codeLabel}</p>
              <div className="rounded-full bg-eoe-blue/10 px-5 py-2 text-2xl font-semibold tracking-[0.2em] text-eoe-blue dark:bg-eoe-blue/20">
                {code.toUpperCase()}
              </div>
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(code).then(() => {
                    toast.success(ui.genericCopied);
                  });
                }}
                className={getQuizButtonClassName({ variant: "secondary" })}
              >
                {ui.copyCodeAction}
              </button>
            </div>
          </div>
        </section>

        <div className="flex flex-col gap-4">
          <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 shadow-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
            <div className="mb-3 flex items-center gap-2 text-eoe-text dark:text-eoe-dark-text">
              <Share2 className="size-4" aria-hidden />
              <h2 className="text-base font-semibold">{ui.shareHeading}</h2>
            </div>
            <div className="space-y-3">
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{shareUrl}</p>
              <ShareActions
                url={shareUrl}
                title={ui.shareTitle}
                text={ui.shareText}
                sourceEntityType="quiz"
                sourceEntityId={code}
                sourceEntityTitle={ui.shareTitle}
              />
            </div>
          </section>

          <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 shadow-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
            <h2 className="mb-3 text-base font-semibold text-eoe-text dark:text-eoe-dark-text">{ui.joinedPlayersTitle}</h2>
            {quiz.participants.length === 0 ? (
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.noPlayersYet}</p>
            ) : (
              <div className="space-y-3">
                {quiz.participants.map((participant) => (
                  <EntityAvatar
                    key={participant.user_id ?? `participant-${participant.joined_at}`}
                    entity={mapQuizUserToEntityAvatarUser(participant.user)}
                    entityType="user"
                    entityId={participant.user_id ?? undefined}
                    entityChip={true}
                    showLink={true}
                    isInputField={false}
                    className="min-w-0"
                  />
                ))}
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-eoe-border-main bg-eoe-bg-modal p-5 shadow-sm dark:border-eoe-dark-border-main dark:bg-eoe-dark-bg-modal">
            {quiz.quiz.is_creator ? (
              <div className="space-y-3">
                {!quiz.quiz.can_start ? (
                  <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.startNeedsOtherPlayer}</p>
                ) : null}
                <button
                  type="button"
                  onClick={onStart}
                  disabled={isStarting || !quiz.quiz.can_start}
                  className={getQuizButtonClassName({
                    disabled: Boolean(isStarting || !quiz.quiz.can_start),
                    fullWidth: true,
                  })}
                >
                  {isStarting ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
                  {ui.startLobbyAction}
                </button>
              </div>
            ) : (
              <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">{ui.waitingForCreator}</p>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default QuizLobby;
